/**
 * Acting business location — one per browser tab.
 *
 * A multi-location user can have several tabs (and devices) open, each at a
 * different location. The backend used to scope location-less requests by a
 * single per-USER value (`staff_users.last_used_salon_id`), so switching
 * location in one tab silently re-scoped every other tab — cross-tab 403s and
 * tabs "flipping" to the most recently picked location after a settings save.
 *
 * Each tab now owns its location: the location uuid lives in sessionStorage
 * (per tab; survives reload and "Duplicate tab") and is sent on every API
 * request as `X-Business-Location-Id`. The backend honors it on every
 * endpoint without an explicit location, and `GET /access` echoes it back as
 * `selected_salon_uuid`.
 *
 * Storage access is wrapped in try/catch: private windows or blocked site
 * data can make sessionStorage throw, in which case the tab simply sends no
 * header and the backend falls back to the remembered location.
 */

export const ACTING_LOCATION_HEADER = "X-Business-Location-Id";

const STORAGE_KEY = "pagoda.actingLocationUuid";
const MOVED_NOTICE_KEY = "pagoda.actingLocationMoved";

export function getActingLocationUuid(): string | null {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setActingLocationUuid(uuid: string): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, uuid);
  } catch {
    // Storage unavailable — requests go out without the header.
  }
}

export function clearActingLocationUuid(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing stored to clear.
  }
}

/**
 * Reconcile this tab's location with `GET /access`'s `selected_salon_uuid`.
 *
 * `/access` echoes the tab's own location when the user is still assigned
 * there, so normally nothing changes. A different (or missing) value means
 * the backend rejected the tab's location — e.g. the assignment was revoked
 * while the tab was open — and the tab adopts what the backend landed on.
 *
 * Returns true when the tab HAD a location and the backend moved it.
 */
export function adoptActingLocation(selectedUuid: string | null): boolean {
  const stored = getActingLocationUuid();
  if (selectedUuid) {
    setActingLocationUuid(selectedUuid);
  } else {
    clearActingLocationUuid();
  }
  return stored !== null && stored !== selectedUuid;
}

/**
 * Remember, across the re-boot reload, that the backend moved this tab — a
 * toast shown right before `window.location.reload()` vanishes unread.
 */
export function flagActingLocationMoved(): void {
  try {
    window.sessionStorage.setItem(MOVED_NOTICE_KEY, "1");
  } catch {
    // Storage unavailable — the reload still happens, just without a notice.
  }
}

/** True (once) when the previous page load was re-booted onto a new location. */
export function consumeActingLocationMoved(): boolean {
  try {
    const moved = window.sessionStorage.getItem(MOVED_NOTICE_KEY) !== null;
    window.sessionStorage.removeItem(MOVED_NOTICE_KEY);
    return moved;
  } catch {
    return false;
  }
}

/** Header object to spread into an authenticated request (empty when unset). */
export function actingLocationHeaders(): Record<string, string> {
  const uuid = getActingLocationUuid();
  return uuid ? { [ACTING_LOCATION_HEADER]: uuid } : {};
}
